import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { Messages, newMessage } from "../../../model/Message";
import { alertOpen, AlertValue } from "../../../features/alert.slice";
import messagesQuery from "../../../firestore/Messages.query";
import { auth } from "../../../firebase";
import { Spiner } from "../../../components/spinner/Spiner";
import { convertDateToJapaneseStringWithWeekZeroPadding, convertTimeToString } from "../../../utility/ConvertUtily";
import { EventParticipationHistory } from "../../../model/EventParticipationHistory";
import Button from "../../../components/button/Button";
import { ConstMessage } from "../../../config/const";
import userQuery from "../../../firestore/User.query";
import { UserData } from "../../../model/Users";
import companiesQuery from "../../../firestore/Companies.query";

export const EventParticipationWebInfo = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  // URLパラメータからmessageId取得
  const { messageId } = useParams<{ messageId: string }>();
  const href = useRef<string>(window.location.href);

  const [isLoad, setIsLoad] = useState(true);
  const [userHistory, setUserHistory] = useState<EventParticipationHistory>();
  const [message, setMessage]  = useState<Messages>(newMessage);
  const [deliveryTargetList, setdeliveryTargetList] = useState<string[]>([]);

  const location = useLocation();

  useEffect(() => {
    const windowBreakPoint = 1024;
    if (href.current.includes('eventParticipationWeb')) {
      if (window.innerWidth < windowBreakPoint) {
        // 画面幅が1024px未満の場合
        navigate(`/portal/eventParticipationMobile/${messageId}/info`);
      }
    } else {
      if (window.innerWidth >= windowBreakPoint) {
        // 画面幅が1024px以上の場合
        navigate(`/portal/eventParticipationWeb/${messageId}/info`);
      }
    }
  },[location.pathname, messageId, navigate]);

  // 初期データ取得
  useMemo(() => {
    const f = async () => {
      try {
        if (messageId) {
          // お知らせ取得
          const message = await messagesQuery.getMessage(messageId);
          // 返信・出欠データ取得
          const history = await messagesQuery.getEventParticipationHistory(currentUser?.tenantId!, currentUser?.displayName!, currentUser?.uid!, messageId);
          setUserHistory(history);

          if (message.select === "topic") {
            // グループ取得
            const topics = await messagesQuery.getTopic(messageId);
            message.topic = topics.map((t) => t.id);
          }

          // 配信先はtokenだがtopicに値が入っている場合はクリア
          if (message.select === "token" && message.topic?.length > 0) {
            message.topic.length = 0;
          }

          // 配信先の判定
          const publishTargetList = message?.topic ?? [];
          const publishTarget = message?.select;

          if (publishTargetList.includes(publishTarget!)) {
            setdeliveryTargetList(['全体']);
          } else if (
            publishTarget === 'topic' &&
            !publishTargetList.includes(publishTarget!)
          ) {
            if (Array.isArray(message.topic)) {
              const groupList = await companiesQuery.getGroupList(message.topic, currentUser?.tenantId!, currentUser?.uid!);
              setdeliveryTargetList(groupList.length === 0 ? ['脱退済みのグループ'] : groupList);
            }
          } else if (
            publishTarget === 'token' ||
            (typeof publishTarget === 'string' && publishTarget.startsWith('token')) ||
            (!publishTarget && publishTargetList.length === 0)
          ) {
            const user = await userQuery.getUser(currentUser?.tenantId!, currentUser?.uid!) as UserData;
            setdeliveryTargetList([user.lastName ?? ""]);
          } else {
            setdeliveryTargetList(['不明な配信先']);
          }
          setMessage(message);

          setIsLoad(false);
        }
      } catch (err :any) {
        console.log(err)
        let errorMessage = err.error;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_CONNECT_NETWORK, "danger")));
        } else {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_EVENT_HISTORY, "danger")));
        }
        setIsLoad(false);
      }
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.tenantId, dispatch]);

  const getDestinatione = () => {
    if (href.current.includes('eventParticipationWeb')) {
      return "/portal/eventParticipationWeb";
    } else {
      return "/portal/eventParticipationMobile";
    }
  };

  return (
    <main>
      <div className="flex flex-col overflow-y-auto mb-5">
        <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
          <button
            className="flex px-4 justify-center font-medium rounded-md shadow transition ease-in duration-200 rounded h-10 px-2 text-sm items-center text-orange-500 bg-white border-2 border-orange-500 hover:bg-orange-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-orange-100"
            onClick={() => navigate(getDestinatione())}
          >
            <ChevronLeftIcon className="-ml-2 h-5 w-5" aria-hidden="true" />
            <span>戻る</span>
          </button>
        </nav>
        {isLoad ?
          <Spiner />
        :
          <div className="max-w-5xl px-3 mb-5">
            <div className="flex flex-col">
              <span>配信 : {convertDateToJapaneseStringWithWeekZeroPadding(new Date(message.sendDate))}</span>
              <span>配信者 : {message.authorName}</span>
              <h2 className="text-2xl font-bold text-gray-900">{message.title}</h2>
              <div className="my-3 border-b" />
              <div className="flex flex-wrap">
                <div className="flex items-center gap-2">
                  <span>日時</span>
                  <span className="text-xl">{convertDateToJapaneseStringWithWeekZeroPadding(new Date(message.eventAt))}</span>
                </div>
                <div className="flex items-center gap-2 ml-10">
                  <span>開始</span>
                  <span className="text-xl">{convertTimeToString(new Date(message.eventAt))}</span>
                  <span>～</span>
                  <span>終了</span>
                  <span className="text-xl">{convertTimeToString(new Date(message?.eventEndAt))}</span>
                </div>
              </div>
              {message.eventVenue && (
                <div>
                  <div className="flex items-center gap-1">開催会場 <span className="text-xl">{message.eventVenue}</span></div>
                </div>
              )}
              <div className="my-3 border-b" />
              {deliveryTargetList.length > 0 && (
                <div className="mb-2 flex gap-2">
                  <span className="whitespace-nowrap">配信先：</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {deliveryTargetList.map((name, index) => (
                      <span key={index} className="whitespace-nowrap">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="my-3 border-b" />
              <span className="text-xl whitespace-pre-line">{message?.body}</span>
              {(message.image) ? <img src={message.image} alt={'添付画像'} className={"self-center w-max mt-3"} /> : <></>}
              {(message.urlButtonFlg) ?
                <Button
                  onClick={() => window.open(message.urlLink, "_blank")}
                  className="h-12 w-2/3 items-center font-bold mt-3 mb-1 self-center"
                  color="darkOrange"
                  children={message.urlButtonName} />
              :
                <></>
              }
              {(message.contactInformation) ?
                <>
                  <div className="my-3 border-b" />
                  <span>問い合わせ先 : <span className="text-xl">{message.contactInformation}</span></span>
                </>
              :
                <></>
              }
              <div className="my-3 border-b" />
              <div className="text-xl">{userHistory?.replyButtonName}</div>
              {userHistory?.attendanceButtonName && (
                <div>
                  出欠結果 : <span className="text-xl">{userHistory.attendanceButtonName}</span>
                </div>
              )}
            </div>
          </div>
        }
      </div>
    </main>
  );
};
